import { LoadingOverlay, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import CustomDropZone from '@/components/CustomDropZone.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import useReplaceStaticDataset from '@/hooks/api/datasets/static/useReplaceStaticDataset.ts';
import FileItem from '@/pages/AutomationPage/components/ManualRunModal/components/FileItem.tsx';
import { RelativeFile } from '@/types/common.ts';
import { StaticDataset } from '@/types/datasets.ts';

type Props = {
  opened: boolean;
  onClose: () => void;
  staticDataset?: StaticDataset;
};

const ReplaceStaticDatasetModal = ({ opened, onClose, staticDataset }: Props) => {
  const [datasetName, setDatasetName] = useState('');
  const [files, setFiles] = useState<RelativeFile[]>([]);

  const handleClose = () => {
    setFiles([]);
    setDatasetName(staticDataset?.dataset.name || '');
    onClose();
  };

  const { mutate: replaceStaticDataset, isPending } = useReplaceStaticDataset({
    onSuccess: handleClose,
  });

  useEffect(() => {
    if (opened) {
      setDatasetName(staticDataset?.dataset.name || '');
      setFiles([]);
    }
  }, [opened, staticDataset?.dataset.name]);

  const confirmDisabled = !staticDataset || files.length === 0 || !datasetName.trim();

  return (
    <StyledModal opened={opened} onClose={handleClose} title="Replace data source">
      <Stack gap="sm" style={{ position: 'relative' }}>
        <LoadingOverlay visible={isPending} zIndex={1000} />

        <Text size="sm" c="dimmed">
          Replacing changes this shared source for every version that uses it. Runs that have
          already captured their inputs keep their existing data. New starts of published versions
          that use this source may be blocked until the updated data is reviewed and published. For
          a draft-only change, upload a new source and select it in the draft.
        </Text>
        <TextInput
          label="Data Source Name"
          placeholder="Data source name"
          size="sm"
          value={datasetName}
          onChange={(event) => setDatasetName(event.currentTarget.value)}
          autoFocus={true}
          data-autofocus
          required={true}
          readOnly={isPending}
        />

        <CustomDropZone mode="multiple" files={files} setFiles={setFiles} height="60px" />

        {files.map((file, idx) => (
          <FileItem
            key={`${file.path}-${idx}`}
            name={file.path}
            size={file.file.size}
            onDelete={() => {
              const newFiles = [...files];
              newFiles.splice(idx, 1);
              setFiles(newFiles);
            }}
          />
        ))}
      </Stack>
      <ModalConfirmationButtons
        confirmText="Replace"
        confirmLoading={isPending}
        confirmDisabled={confirmDisabled}
        closeOnConfirm={false}
        onClose={handleClose}
        onConfirm={() => {
          if (!staticDataset) {
            return;
          }
          replaceStaticDataset({
            staticDatasetId: staticDataset.id,
            name: datasetName,
            files: [...files],
          });
        }}
      />
    </StyledModal>
  );
};

export default ReplaceStaticDatasetModal;
